import config from '@/config'
import { encryptionAES, sha256WithRSA, uuid, decryptAES } from '@/utils/crypto'
import { getObjTypeStorage } from '@/utils'
import { useAppStore } from '@/pinia'
import qs from 'qs'
import { antiShake } from '@/utils/antiShakingAndThrottling'
import { ajax } from '@/service/base/axiosAjax'
import Tips from '@/service/base/tips'
import { clipboard, toLogin } from '@/utils'
import { router } from '@/router'
import { shouldEncrypt } from '@/constants/encrypt-whitelist'
import { showConfirmDialog } from 'vant'
// 加密解密开关
const VITE_USE_CRYPTION = import.meta.env.VITE_USE_CRYPTION

// 口令封装处理
const handlerToken = (header = {}) => {
  const loginToken = sessionStorage.getItem('loginToken') || ''
  if (loginToken) {
    header['loginToken'] = loginToken
  }

  return header
}
// 401退出登录
const noLoginHandle = antiShake(() => {
  // toLogin()
}, 1000)

// 没有token跳转到登录页
export const isHasToken = () => {
  const loginToken = sessionStorage.getItem('loginToken') || ''
  if (loginToken) return true
  antiShake(() => {
    toLogin()
  })()
}

// 处理opt传入参数
const handlerData = (opt, apiBase = {}) => {
  const { prefix } = apiBase,
    isMock = opt.mock ?? import.meta.env.VITE_APP_ENV === 'mock' // 是否为mock模式

  if (isMock) {
    opt.url = `/mock${prefix}${opt.url}`
  } else if (!~opt.url.indexOf('http')) {
    // 根据环境变量判断是否使用代理
    const isDev = import.meta.env.VITE_APP_ENV
    opt.baseURL = isDev === 'development' ? '/api' : config.domainName
    opt.url = `${prefix}${opt.url}`
  }

  opt.headers = opt.headers ?? { 'Content-Type': 'application/json' } // 设置默认headers
  opt.headers = handlerToken(opt.headers)
  opt.file = opt.file ?? false // 是否为文件模式，文件下载模式为后端直接下载文件，不做处理判断
  opt.mock = isMock
  // opt.responseType = opt.responseType ?? (opt.mock ? 'json' : 'text') // 细节需要加括号,上环境情况下后端返回的数据是base64字符串
  opt.responseType = opt.responseType ?? 'json'
  opt.reLogin = opt.reLogin ?? true // 是否判断401状态跳转到登录页面

  return opt
}
// 错误信息
const handlerErrorMessage = (error, message, tipsCode, isShowToast = true) => {
  if (
    message?.includes('参数校验错误') ||
    message?.includes('未知系统异常') ||
    message?.includes('访问异常，请稍后重试!') ||
    tipsCode === 'MOB2230029'
  ) {
    return
  }
  // 错误码 10028 由业务代码（退票页通用错误提醒弹窗）自己处理，不显示 toast
  if (tipsCode === '10028' || tipsCode === 10028) {
    return
  }
  // 错误码 60002 由业务代码（退票页通用错误提醒弹窗）自己处理，不显示 toast
  if (tipsCode === '60002' || tipsCode === 60002) {
    return
  }
  if (tipsCode === 'CTG0000000') {
    message = '系统异常，请联系客服'
  }

  // 特殊订单处理：如果tipsCode为ORDER_CHANGE_PAY_ERROR，则弹出弹窗展示错误message
  if (tipsCode === 'ORDER_CHANGE_PAY_ERROR') {
    const errorMsg = error !== true ? error : (message ?? '网络异常，请稍后重试')
    showConfirmDialog({
      message: errorMsg,
      confirmButtonText: '好的',
      showCancelButton: false,
      className: 'pay-error-dialog',
      width: '300px',
    })
    return
  }

  // 如果错误信息包含"非该登录用户"，在app环境下重定向到订单列表页面
  if (message?.includes('非该登录用户') && window.flutter_inappwebview) {
    // 延迟执行重定向，确保用户能看到错误提示
    setTimeout(() => {
      // 在app环境下使用H5路由跳转到订单列表
      router.replace('/orderlist')
    }, 3000)
  }
  console.log('handlerErrorMessage', error, isShowToast, message, tipsCode);
  error && isShowToast &&
    Tips.error({ msg: error !== true ? error : (message ?? '网络异常，请稍后重试'), tipsCode })
}
// 成功信息
const handlerSuccessMessage = (success, message, tipsCode = '') => {
  success &&
    Tips.success({
      msg: success !== true ? success : (message ?? '成功'),
      tipsCode,
    })
}

// 业务接口
async function request(
  opt = {},
  {
    prefix = '',
    codeField = 'success',
    dataField = 'data',
    codeNum = true,
    msgField = 'message',
    tipsCode = 'errorCode',
    needEncrypt = true, // 默认需要加密，不用设置
  }
) {
  opt = handlerData(opt, { prefix }) // 参数预处理
  const error = opt.error ?? true // 成功提醒
  const success = opt.success ?? false // 错误提醒

  // 特殊格式请求处理
  const posts = ['put', 'post', 'patch']
  if (
    posts.includes(opt.method) &&
    opt.headers['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    opt.data = qs.stringify(opt.data)
  }
  const isThirdApi = ~opt.url.indexOf('http') //请求地址http开头的第三方接口
  // 检查接口是否在白名单中（需要加密）
  const needEncryptByWhitelist = shouldEncrypt(opt.url)
  // 最终是否需要加密：needEncrypt 参数为 true 且接口在白名单中
  const finalNeedEncrypt = needEncrypt && needEncryptByWhitelist

  // 加密解密开关
  if (VITE_USE_CRYPTION === 'true') {
    // 非第三方api，且需要加密（通过参数和白名单判断）
    if (!isThirdApi && finalNeedEncrypt) {
      // 生成请求参数
      const requestUuid = uuid() // 重命名变量避免冲突
      const timestamp = new Date().getTime().toString()
      const version = '1.0'
      const systemId = '2201'

      // 只加密 body 内容
      const encryptedBody = encryptionAES(opt.data)

      // 生成签名
      const signStr = sha256WithRSA({
        systemId,
        uuid: requestUuid,
        timestamp,
        versionId: version,
      })

      // 构造新的请求体
      opt.data = {
        systemId,
        sign: signStr,
        uuid: requestUuid,
        timestamp,
        version,
        body: encryptedBody,
      }
    }
  }

  try {
    const result = await ajax(opt) // 请求接口
    if (result.headers['authorization']) {
      const appStore = useAppStore()
      appStore.setItem({ SET_TOKEN: result.headers['authorization'] })
    }
    const resData = result?.data ?? {}
    // if ((opt.reLogin && result.status === 401) || resData.errorCode === 'MOB2230010' ||  resData.errorCode ==='30010' ) {
    if (
      (opt.reLogin && result.status === 401) ||
      resData.errorCode === 'MOB2230010' ||
      resData.errorCode === '30010'
    ) {
      noLoginHandle()
      return Promise.reject(result)
    }

    switch (opt.file) {
      case false: {
        let response = result.data
        // 加密解密开关
        if (VITE_USE_CRYPTION === 'true' && finalNeedEncrypt) {
          // 需要解密
          if (response?.data?.body) {
            // 只解密 body 内容
            response = decryptAES(response.data.body)
            if (!response) {
              return Promise.reject(result)
            }
          }
        }
        const code = result.data[codeField]
        // 如果 dataField 为空字符串，返回完整的响应对象；否则提取指定字段
        const data = dataField === '' ? result.data : result.data[dataField]
        const message = result.data[msgField]
        const errCode = result.data[tipsCode]
        if (code === codeNum) {
          // 提前处理正确错误
          handlerSuccessMessage(success, message)
          return Promise.resolve(VITE_USE_CRYPTION === 'true' && finalNeedEncrypt ? response : data)
        } else {
          handlerErrorMessage(error, message, errCode, opt.isShowToast)
   
          // 如果 error: false，返回包含错误信息的对象，让业务代码自己判断；否则 reject
          if (error === false) {
            // 如果 dataField 为空字符串，result.data 已经是完整的响应对象，直接返回
            // 否则构造一个包含错误信息的对象
            return Promise.resolve(
              dataField === ''
                ? result.data
                : {
                    success: false,
                    message: message || '请求失败',
                  }
            )
          }

          return Promise.reject(response)
        }
      }
      // 走文件模式下
      case true: {
        return Promise.resolve(result)
      }
    }
  } catch (e) {
    const response = e.response
    if (opt.reLogin && response?.status === 401) noLoginHandle()
    else {
      const resData = response?.data ?? {}
      const message = resData[msgField]
      const errCode = resData[tipsCode]
      handlerErrorMessage(error, message, errCode, opt.isShowToast)
    }

    return Promise.reject(e)
  }
}

// 业务接口，返回整个接口返回结果
async function requestWhole(
  opt = {},
  {
    prefix = '',
    codeField = 'success',
    codeNum = true,
    msgField = 'message',
    tipsCode = 'errorCode',
  }
) {
  opt = handlerData(opt, { prefix }) // 参数预处理
  const error = opt.error ?? true // 成功提醒
  const success = opt.success ?? false // 错误提醒

  // 特殊格式请求处理
  const posts = ['put', 'post', 'patch']
  if (
    posts.includes(opt.method) &&
    opt.headers['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    opt.data = qs.stringify(opt.data)
  }

  try {
    const result = await ajax(opt) // 请求接口
    if (result.headers['authorization']) {
      const appStore = useAppStore()
      appStore.setItem({ SET_TOKEN: result.headers['authorization'] })
    }
    const resData = result?.data ?? {}
    // if ((opt.reLogin && result.status === 401) || resData.errorCode === 'MOB2230010' ||  resData.errorCode ==='30010' ) {
    if (
      (opt.reLogin && result.status === 401) ||
      resData.errorCode === 'MOB2230010' ||
      resData.errorCode === '30010'
    ) {
      noLoginHandle()
      return Promise.reject(result)
    }

    let response = result.data
    const code = result.data[codeField]
    const message = result.data[msgField]
    const errCode = result.data[tipsCode]
    if (code === codeNum) {
      // 提前处理正确错误
      handlerSuccessMessage(success, message)
      return Promise.resolve(response)
    } else {
      handlerErrorMessage(error, message, errCode, opt.isShowToast)
      return Promise.reject(response)
    }
  } catch (e) {
    const response = e.response
    if (opt.reLogin && response?.status === 401) noLoginHandle()
    else {
      const resData = response?.data ?? {}
      const message = resData[msgField]
      const errCode = resData[tipsCode]
      handlerErrorMessage(error, message, errCode, opt.isShowToast)
    }

    return Promise.reject(e)
  }
}

export { request, requestWhole }
